/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { debounce, omit } from './lodash';

export default function(defaultDelay = 0) {
	return Comp =>
		class DebounceInput extends Component {
			static propTypes = {
				...Comp.propTypes,
				delay: PropTypes.number,
				value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
				onKeyDown: PropTypes.func,
				onChange: PropTypes.func,
				onBlur: PropTypes.func,
				onClear: PropTypes.func,
			};

			static defaultProps = {
				...Comp.defaultProps,
				delay: defaultDelay,
			};

			state = {
				value: this.props.value,
			};

			_debouncer = null;

			_refreshDebouncer = true;

			componentWillReceiveProps({ value, delay }) {
				if (delay !== this.props.delay) {
					this._refreshDebouncer = true;
				}

				if (value === this.state.value || value !== this.props.value) {
					this._debouncer && this._debouncer.cancel();
					this.setState({ value });
				}
			}

			componentWillUnmount() {
				this._debouncer && this._debouncer.cancel();
			}

			focus() {
				this._input && this._input.focus && this._input.focus();
			}

			_notify = value => {
				this.props.onChange && this.props.onChange(value);
			};

			_onKeyDown = e => {
				if (e.keyCode === 13) {
					this._debouncer && this._debouncer.flush();
				}

				this.props.onKeyDown && this.props.onKeyDown(e);
			};

			_onBlur = e => {
				this._debouncer && this._debouncer.flush();
				this.props.onBlur && this.props.onBlur(e);
			};

			_onChange = value => {
				this.setState({ value });

				if (this._refreshDebouncer) {
					const { delay } = this.props;
					this._debouncer && this._debouncer.cancel();
					this._debouncer = debounce(this._notify, delay);
					this._refreshDebouncer = false;
				}

				this._debouncer(value);
			};

			_onClear = () => {
				this._debouncer && this._debouncer.flush();
				this.props.onClear && this.props.onClear();
			};

			_ref = input => {
				this._input = input;
			};

			render() {
				const other = omit(this.props, 'delay');
				const { value } = this.state;

				return (
					<Comp
						{...other}
						ref={this._ref}
						value={value}
						onBlur={this._onBlur}
						onClear={this._onClear}
						onChange={this._onChange}
						onKeyDown={this._onKeyDown}
					/>
				);
			}
		};
}
